<template>
  <div class="form-section mb-0 p-4">
    <wz-alert variant="info" icon="ri-information-line" class="mb-4">
      Los documentos se subirán después de guardar el colaborador. Si algún archivo falla, se puede
      completar desde el detalle del colaborador (pestaña Documentos).
    </wz-alert>

    <div class="flex justify-between items-center mb-3">
      <h2 class="form-subtitle mb-0">Legajo digital</h2>
      <wz-button variant="primary" :disabled="allTypesBuffered" @click="openAddModal">
        <i class="ri-upload-2-line mr-1"></i> Agregar documento
      </wz-button>
    </div>

    <div v-if="documents.length === 0" class="text-center py-4 text-muted">
      No hay documentos agregados
    </div>

    <div v-else class="overflow-x-auto">
    <table class="wz-table w-full">
      <thead>
        <tr>
          <th>Tipo</th>
          <th>Archivo</th>
          <th>Tamaño</th>
          <th>Observaciones</th>
          <th class="text-right">Opciones</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="d in documents" :key="d._tid">
          <td>{{ d.documentTypeName }}<small v-if="d.signed" class="block text-muted">Firmado</small></td>
          <td>
            <span class="flex items-center">
              <img v-if="d.preview" :src="d.preview" alt="preview"
                   style="width:28px;height:28px;object-fit:cover;border-radius:4px;margin-right:8px" />
              <i v-else class="ri-file-pdf-line mr-2" style="font-size:20px;color:#d33"></i>
              <span :title="d.file.name">{{ truncate(d.file.name) }}</span>
            </span>
          </td>
          <td>{{ formatSize(d.file.size) }}</td>
          <td>{{ d.notes || '—' }}</td>
          <td class="text-right">
            <wz-button v-if="d.preview" variant="outline" size="sm" class="mr-1" @click="openPreview(d)">Ver</wz-button>
            <wz-button variant="outline-danger" size="sm" @click="removeDoc(d)">Quitar</wz-button>
          </td>
        </tr>
      </tbody>
    </table>
    </div>

    <!-- Modal agregar documento al buffer -->
    <wz-modal v-model="showModal" title="Agregar documento" @close="resetForm">
      <div class="mb-3">
        <label>Tipo de documento *</label>
        <wz-select
          v-model="form.documentTypeId"
          :items="availableDocumentTypes"
          defaultOption="Elegir tipo de documento"
        />
      </div>

      <div class="mb-3">
        <label>Archivo * <small class="text-muted">(PDF, JPG, JPEG, PNG — máx. 25MB)</small></label>
        <wz-file-input
          :key="fileKey"
          class="w-full mt-2"
          id-file="employee-doc-draft"
          :accept-file="['.pdf', '.jpg', '.jpeg', '.png']"
          @onChange="onFileChange"
        />
      </div>

      <div class="mb-3">
        <label>Observaciones</label>
        <textarea v-model="form.notes" class="wz-form-control w-full" rows="3"
                  placeholder="Ingresar observaciones (opcional)" />
      </div>

      <UploadSignedQuestion
        v-model="form.signed"
        :required="formTypeRequiresSignature"
        :type-id="form.documentTypeId"
      />

      <wz-alert v-if="allTypesBuffered" variant="info" icon="ri-checkbox-circle-line" class="mb-3">
        Todos los tipos de documento ya fueron agregados.
      </wz-alert>

      <div class="flex justify-end mt-3">
        <wz-button variant="light" class="mr-2" @click="showModal = false">Cancelar</wz-button>
        <wz-button variant="primary" @click="addDoc">Agregar</wz-button>
      </div>
    </wz-modal>

    <!-- Preview (imágenes) -->
    <wz-modal v-model="showPreview" :title="previewTitle" size="xl" @close="onPreviewHidden">
      <div class="text-center">
        <img v-if="previewUrl" :src="previewUrl" class="img-fluid" style="max-height:75vh" />
      </div>
    </wz-modal>
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import { DocumentTypeService } from '@/core/services'
import UploadSignedQuestion from '../../components/UploadSignedQuestion.vue'

const ALLOWED = ['pdf', 'jpg', 'jpeg', 'png']
const MAX_SIZE = 25 * 1024 * 1024 // 25MB

export default {
  name: 'DocumentsDraftTab',
  components: { UploadSignedQuestion },
  props: {
    // v-model: lista de documentos en buffer (se suben tras crear el colaborador).
    value: { type: Array, default: () => [] },
  },
  data() {
    return {
      documents: [],
      documentTypeList: [],
      tidSeq: 1,
      showModal: false,
      fileKey: 0,
      form: { documentTypeId: null, file: null, notes: '', signed: false },
      showPreview: false,
      previewUrl: null,
      previewTitle: '',
    }
  },
  computed: {
    bufferedTypeIds() {
      return this.documents.map(d => d.documentTypeId)
    },
    availableDocumentTypes() {
      return this.documentTypeList.filter(t => !this.bufferedTypeIds.includes(t.key))
    },
    formTypeRequiresSignature() {
      const type = this.documentTypeList.find(t => t.key === this.form.documentTypeId)
      return !!(type && type.requiresSignature)
    },
    allTypesBuffered() {
      return this.documentTypeList.length > 0 && this.availableDocumentTypes.length === 0
    },
  },
  async created() {
    // Hidrata desde v-model (por si el padre trae buffer previo).
    this.documents = (this.value || []).map(d => ({ ...d, _tid: this.tidSeq++, preview: this.makePreview(d.file) }))
    try {
      const resp = (await DocumentTypeService.list()).payload || []
      this.documentTypeList = resp.map(d => ({ key: d.id, label: d.name, requiresSignature: d.requiresEmployeeSignature }))
    } catch (ex) {
      console.error(ex)
    }
  },
  beforeDestroy() {
    this.documents.forEach(d => { if (d.preview) window.URL.revokeObjectURL(d.preview) })
    if (this.previewUrl) window.URL.revokeObjectURL(this.previewUrl)
  },
  methods: {
    onFileChange(files) {
      this.form.file = files && files.length > 0 ? files[0] : null
    },
    extensionOf(name) {
      const i = (name || '').lastIndexOf('.')
      return i >= 0 ? name.slice(i + 1).toLowerCase() : ''
    },
    makePreview(file) {
      if (!file) return null
      const ext = this.extensionOf(file.name)
      return ['jpg', 'jpeg', 'png'].includes(ext) ? window.URL.createObjectURL(file) : null
    },
    addDoc() {
      if (!this.form.documentTypeId) { Alert.error('Selecciona el tipo de documento'); return }
      if (!this.form.file) { Alert.error('Selecciona un archivo'); return }
      if (this.form.signed === null) { Alert.error('Indica si el archivo ya está firmado por el colaborador'); return }
      const ext = this.extensionOf(this.form.file.name)
      if (!ALLOWED.includes(ext)) { Alert.error('Extensión no permitida. Solo PDF, JPG, JPEG, PNG.'); return }
      if (this.form.file.size > MAX_SIZE) { Alert.error('El archivo supera los 25 MB.'); return }
      if (this.bufferedTypeIds.includes(this.form.documentTypeId)) {
        Alert.error('Ya agregaste un documento de ese tipo.'); return
      }
      const type = this.documentTypeList.find(t => t.key === this.form.documentTypeId)
      this.documents.push({
        _tid: this.tidSeq++,
        documentTypeId: this.form.documentTypeId,
        documentTypeName: type ? type.label : '',
        file: this.form.file,
        notes: this.form.notes || null,
        signed: this.form.signed,
        preview: this.makePreview(this.form.file),
      })
      this.showModal = false
      this.resetForm()
      this.emitChange()
    },
    removeDoc(d) {
      if (d.preview) window.URL.revokeObjectURL(d.preview)
      this.documents = this.documents.filter(i => i._tid !== d._tid)
      this.emitChange()
    },
    emitChange() {
      // Emite la lista limpia (lo que el padre necesita para subir + para el mensaje de fallo parcial).
      const clean = this.documents.map(d => ({
        documentTypeId: d.documentTypeId,
        documentTypeName: d.documentTypeName,
        file: d.file,
        notes: d.notes,
        signed: d.signed,
      }))
      this.$emit('input', clean)
    },
    openAddModal() {
      this.resetForm()
      this.showModal = true
    },
    resetForm() {
      this.form = { documentTypeId: null, file: null, notes: '', signed: false }
      this.fileKey += 1 // re-monta el wz-file-input para limpiar el archivo
    },
    openPreview(d) {
      this.previewUrl = d.preview
      this.previewTitle = d.file.name
      this.showPreview = true
    },
    onPreviewHidden() {
      this.showPreview = false
    },
    truncate(name) {
      return name && name.length > 40 ? name.slice(0, 37) + '...' : name
    },
    formatSize(bytes) {
      if (!bytes) return '—'
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },
  },
}
</script>

<style lang="stylus" scoped></style>
